import React from "react";
import { Chart } from "react-chartjs-2";
import "./chartjs.config"; // Import Chart.js registration
import { formatNumber } from "../../../utils/helper";

const CustomChart = ({
  type = "bar",
  xData = [],
  yData = [],
  label = "Dataset",
  color = "rgba(54, 162, 235, 0.5)",
  color2 = "rgba(255, 99, 132, 0.5)",
  horizontal = false,
  barGap = 0.8,
  placeholderColor = "rgba(200, 200, 200, 0.2)",
  showPlaceholder = true,
  showLabels = false,
  yTicks = false,
  differentColors = false,
  xgrid = false,
  ygrid = false,
  tension = 0,
  isMaxValue = false,
  centerText = false,
  labelFontSize = "10px",
  sideLabels = false,
  perfixbar = "",
  showValuesOnBar = false,
  showHeader = null,
}) => {
  const maxValue = Math.ceil(Math.max(...yData, 1) / 10) * 10;

  const generateColors = (count) => {
    const baseColors = [
      "rgba(255, 99, 132, 0.6)",
      "rgba(54, 162, 235, 0.6)",
      "rgba(255, 206, 86, 0.6)",
      "rgba(75, 192, 192, 0.6)",
      "rgba(153, 102, 255, 0.6)",
      "rgba(255, 159, 64, 0.6)",
    ];
    return Array.from({ length: count }, (_, i) => baseColors[i % baseColors.length]);
  };

  // ✅ Handle Doughnut / Pie chart
  if (type === "pie" || type === "doughnut") {
    const sliceColors =
      xData.length === 2 ? [color, color2] : generateColors(xData.length);

    const datasets = [
      {
        label,
        data: yData,
        backgroundColor: sliceColors,
        borderColor: "#fff",
        borderWidth: 2,
        hoverOffset: 10,
      },
    ];

    // ✅ Calculate Used % for center text
    const total = yData.reduce((a, b) => a + b, 0);
    const used = yData[0] || 0;
    const usedPercent = total > 0 ? ((used / total) * 100).toFixed(1) : 0;

    // ✅ Plugin to draw center text & outside slice labels
    const labelAndCenterPlugin = {
      id: "labelAndCenterPlugin",
      afterDraw(chart) {
        const { ctx } = chart;
        const meta = chart.getDatasetMeta(0);
        const { width, height, top } = chart.chartArea;

        ctx.save();
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";

        // ✅ Outside labels for each slice
        meta.data.forEach((arc, i) => {
          // check whether to show label or not
          if (!sideLabels) {
            return;
          }

          const { startAngle, endAngle } = arc;
          const angle = (startAngle + endAngle) / 2;
          const radius = arc.outerRadius + 20; // position outside the arc
          const x = arc.x + Math.cos(angle) * radius;
          const y = arc.y + Math.sin(angle) * radius;

          const percent =
            total > 0 ? ((yData[i] / total) * 100).toFixed(1) : 0;

          ctx.font = "bold 11px Plus Jakarta Sans";
          ctx.fillStyle = "#111827";
          ctx.fillText(`${xData[i]}`, x, y);
        });

        // ✅ Center text (Used %)
        if (type === "doughnut" && centerText) {
          ctx.font = "bold 22px Plus Jakarta Sans";
          ctx.fillStyle = "#111827";
          ctx.fillText(`${usedPercent}%`, width / 2, top + height / 2);

          ctx.font = "500 12px Plus Jakarta Sans";
          ctx.fillStyle = "#6B7280";
          ctx.fillText("Used", width / 2, top + height / 2 + 16);
        }

        ctx.restore();
      },
    };

    // ✅ Label list below chart
    const valueLabels = xData.map((x, i) => ({
      label: x,
      value: yData[i],
      color: sliceColors[i],
    }));

    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          
        }}
      >
        {showHeader && (
          <div style={{ 
            width: "100%",
            paddingBottom: "16px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "8px"
          }}>
            <span style={{
              fontFamily: "Plus Jakarta Sans",
              fontSize: "16px",
              fontWeight: 700,
              color: "#111827"
            }}>
              {showHeader}
            </span>
            <span style={{
              fontFamily: "Plus Jakarta Sans",
              fontSize: "16px",
              fontWeight: 600,
              color: "#111827"
            }}>
              {formatNumber(total)}{perfixbar}
            </span>
          </div>
        )}
        <div style={{ flex: 9 }}>
          <Chart
            type={type}
            data={{ labels: xData, datasets }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              cutout: '60%', // Adjust this value to change thickness (higher = thinner ring)
              plugins: {
                legend: { display: false },
                title: { display: false },
              },
            }}
            plugins={[labelAndCenterPlugin]}
          />
        </div>

        {showLabels && (
          <div
            style={{
              flex: 1,
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: "10px",
              alignItems: "center",
              paddingTop: "8px",
            }}
          >
            {valueLabels.map((item, idx) => (
              <div
                key={idx}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  fontSize: labelFontSize,
                  color: "#4B5563",
                  fontFamily: "Plus Jakarta Sans",
                }}
              >
                <div
                  style={{
                    width: "12px",
                    height: "12px",
                    backgroundColor: item.color,
                    borderRadius: "2px",
                  }}
                />
                <span>{item.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ✅ Plugin to show values on top of bars
  const barLabelPlugin = showValuesOnBar && {

    id: "barLabelPlugin",
    afterDatasetsDraw(chart) {
      const { ctx } = chart;
      const meta = chart.getDatasetMeta(0); // Get first dataset (actual data)
      ctx.save();
      ctx.font = "600 11px 'Plus Jakarta Sans'";
      ctx.textAlign = "center";
      ctx.fillStyle = "#111827";

      meta.data.forEach((bar, i) => {
        const value = chart.data.datasets[0].data[i];
        if (value != null) {
          const text = formatNumber(value) + perfixbar;
          const textY = Math.max(bar.y - 8, 12);
          ctx.fillText(text, bar.x, textY);
        }
      });
      ctx.restore();
    },
  };

  // ✅ Non-pie/doughnut charts (unchanged)
  const barColors = differentColors
    ? generateColors(xData.length)
    : new Array(xData.length).fill(color);

  const datasets = [
    {
      label,
      data: yData,
      backgroundColor: barColors,
      borderColor: barColors.map((c) => c.replace("0.5", "1")),
      borderWidth: 1,
      barPercentage: barGap,
      categoryPercentage: barGap,
      stack: type !== "doughnut" && type !== "pie" ? "stack1" : undefined,
      tension,
    },
  ];

  if (showPlaceholder) {
    datasets.push({
      label: "placeholder",
      data: new Array(xData.length).fill(maxValue),
      backgroundColor: placeholderColor,
      borderWidth: 0,
      barPercentage: barGap,
      categoryPercentage: barGap,
      stack: type !== "doughnut" && type !== "pie" ? "stack1" : undefined,
    });
  }

  const options = {
    indexAxis: horizontal ? "y" : "x",
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: { top: 30 }, // Add padding for labels on top of bars
    },
    plugins: {
      legend: { display: false },
      title: { display: false },
      tooltip: {
        callbacks: {
          label: function(context) {
            const value = horizontal ? context.parsed.x : context.parsed.y;
            const label = context.dataset.label || '';
            const suffix = perfixbar || '';
            return label ? `${label}: ${value}${suffix}` : `${value}${suffix}`;
          }
        }
      },
    },
    scales: {
      x: {
        grid: { display: xgrid },
        ticks: { display: true },
        border: { display: false },
        stacked: true,
      },
      y: {
        grid: { display: ygrid },
        ticks: { display: yTicks },
        border: { display: false },
        stacked: true,
        max: isMaxValue ? maxValue : undefined,
      },
    },
  };

  // Calculate total sum of values
  const totalSum = yData.reduce((a, b) => a + b, 0);

  const chartComponent = <Chart type={type} data={{ labels: xData, datasets }} options={options} plugins={[barLabelPlugin]} />;

  // If showHeader is provided, wrap chart with header
  if (showHeader) {
    return (
      <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
        <div style={{ 
          paddingBottom: "16px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: "8px"
        }}>
          <span style={{
            fontFamily: "Plus Jakarta Sans",
            fontSize: "16px",
            fontWeight: 700,
            color: "#111827"
          }}>
            {showHeader}:
          </span>
          <span style={{
            fontFamily: "Plus Jakarta Sans",
            fontSize: "16px",
            fontWeight: 600,
            color: "#111827"
          }}>
            {formatNumber(totalSum)}{perfixbar}
          </span>
        </div>
        <div style={{ flex: 1 }}>
          {chartComponent}
        </div>
      </div>
    );
  }

  return chartComponent;
};

export default CustomChart;
